import React, { useEffect, useMemo, useState } from "react";
import SidebarField from "../../components/Sidebar/SidebarField";
import EarningDetails from "./Orders/EarningDetails";
import earningApis from "../../apis/earning.apis";
import { formattedAmount } from "../../utils/helpers/Functions.helper";

const getDecimalValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "object" && value?.$numberDecimal) {
    return Number(value.$numberDecimal);
  }

  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? null : parsedValue;
};

const formatDate = (value) => {
  if (!value) {
    return "N/A";
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const extractListFromResponse = (responseData) => {
  if (Array.isArray(responseData)) {
    return responseData;
  }

  const data = responseData?.data ?? responseData;
  const possibleKeys = [
    "vendorPayouts",
    "payouts",
    "items",
    "docs",
    "results",
    "rows",
  ];

  for (const key of possibleKeys) {
    if (Array.isArray(data?.[key])) {
      return data[key];
    }
  }

  return [];
};

const extractObjectFromResponse = (responseData) => {
  if (!responseData) {
    return null;
  }

  if (responseData?.data && !Array.isArray(responseData.data)) {
    return responseData.data;
  }

  return responseData;
};

const getVendorId = (earningData) =>
  earningData?.orderDetails?.vendor?._id ||
  earningData?.orderDetails?.vendor?.vendor_id ||
  earningData?.vendor_id ||
  earningData?.vendorId;

const getPayoutId = (payout) =>
  payout?._id || payout?.payout_id || payout?.payoutId || payout?.id;

const getPayoutAmount = (payout) =>
  getDecimalValue(
    payout?.total_amount ||
      payout?.amount ||
      payout?.payout_amount ||
      payout?.payable_amount ||
      payout?.total
  );

const getPayoutDate = (payout) =>
  payout?.payout_date ||
  payout?.createdAt ||
  payout?.date ||
  payout?.paid_at ||
  payout?.updatedAt;

const getPayoutStatus = (payout) =>
  payout?.status || payout?.payout_status || payout?.payment_status || "N/A";

const getVendorName = (payout) => {
  const firstName = payout?.vendor_id?.first_name || "";
  const lastName = payout?.vendor_id?.last_name || "";
  const fullName = `${firstName} ${lastName}`.trim();

  return fullName || "N/A";
};

const getPaidByName = (payout) => {
  const firstName = payout?.paid_by?.first_name || "";
  const lastName = payout?.paid_by?.last_name || "";
  const fullName = `${firstName} ${lastName}`.trim();

  return fullName || payout?.paid_by?.email || "N/A";
};

const formatAmount = (value) => {
  const amount = getDecimalValue(value);
  return amount === null ? "N/A" : formattedAmount(amount);
};

const toReadableLabel = (value) =>
  value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const DetailValue = ({ label, value }) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (Array.isArray(value)) {
    return (
      <div className="space-y-2">
        <p className="text-sm font-semibold">{label}</p>
        {value.length ? (
          value.map((item, index) => (
            <div key={`${label}-${index}`} className="rounded-lg border p-3">
              <DetailObject data={item} />
            </div>
          ))
        ) : (
          <p className="text-sm text-gray500">No data available</p>
        )}
      </div>
    );
  }

  if (typeof value === "object") {
    return (
      <div className="space-y-2">
        <p className="text-sm font-semibold">{label}</p>
        <div className="rounded-lg border p-3">
          <DetailObject data={value} />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border p-3">
      <p className="text-xs uppercase tracking-wide text-gray500">{label}</p>
      <p className="mt-1 text-sm font-medium break-words">{String(value)}</p>
    </div>
  );
};

const DetailObject = ({ data }) => {
  if (!data || typeof data !== "object") {
    return <p className="text-sm">{String(data ?? "N/A")}</p>;
  }

  return (
    <div className="grid gap-3">
      {Object.entries(data).map(([key, value]) => (
        <DetailValue key={key} label={toReadableLabel(key)} value={value} />
      ))}
    </div>
  );
};

const InfoCard = ({ label, value }) => (
  <div className="rounded-lg border p-3">
    <p className="text-xs uppercase tracking-wide text-gray500">{label}</p>
    <p className="mt-1 text-sm font-medium break-words">{value || "N/A"}</p>
  </div>
);

const PersonCard = ({ title, name, email, phone }) => (
  <div className="rounded-xl border p-4">
    <p className="text-xs uppercase tracking-wide text-gray500">{title}</p>
    <p className="mt-2 text-sm font-semibold">{name || "N/A"}</p>
    <p className="mt-1 text-sm break-all">{email || "N/A"}</p>
    <p className="mt-1 text-sm">{phone || "N/A"}</p>
  </div>
);

const OrderCard = ({ order }) => {
  const customerName = order?.delivery_address?.name || "N/A";
  const customerPhone = order?.delivery_address?.phone_number || "N/A";
  const addressParts = [
    order?.delivery_address?.flat_no,
    order?.delivery_address?.street,
    order?.delivery_address?.landmark,
    order?.delivery_address?.pin_code,
  ].filter(Boolean);

  return (
    <div className="rounded-xl border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-gray500">Order Id</p>
          <p className="mt-1 text-sm font-semibold">{order?.order_id || "N/A"}</p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wide text-gray500">Payment</p>
          <p className="mt-1 text-sm font-semibold">
            {order?.pay_type || "N/A"} / {order?.payment_status || "N/A"}
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <InfoCard label="Order Date" value={formatDate(order?.order_date)} />
        <InfoCard label="Created At" value={formatDate(order?.createdAt)} />
        <InfoCard label="Item Price" value={formatAmount(order?.item_price)} />
        <InfoCard
          label="Delivery Price"
          value={formatAmount(order?.total_delivery_price)}
        />
        <InfoCard
          label="Total Item Price"
          value={formatAmount(order?.total_item_price)}
        />
        <InfoCard label="Net Total" value={formatAmount(order?.net_total)} />
      </div>

      <div className="mt-4 rounded-lg bg-gray100 p-3">
        <p className="text-xs uppercase tracking-wide text-gray500">Customer</p>
        <p className="mt-1 text-sm font-semibold">{customerName}</p>
        <p className="mt-1 text-sm">{customerPhone}</p>
        <p className="mt-2 text-sm break-words">
          {addressParts.length ? addressParts.join(", ") : "N/A"}
        </p>
      </div>
    </div>
  );
};

const VendorPayoutDetailsView = ({ details }) => {
  const payout = details?.payout;
  const summary = details?.summary;

  if (!payout) {
    return <DetailObject data={details} />;
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-2">
        <InfoCard label="Payout Id" value={payout?._id} />
        <InfoCard label="Status" value={payout?.status} />
        <InfoCard label="Payout Date" value={formatDate(payout?.payout_date)} />
        <InfoCard label="Payment Method" value={payout?.payment_method} />
        <InfoCard
          label="Transaction Reference"
          value={payout?.transaction_reference || "N/A"}
        />
        <InfoCard
          label="Total Payout Amount"
          value={formatAmount(payout?.total_amount)}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <PersonCard
          title="Vendor"
          name={getVendorName(payout)}
          email={payout?.vendor_id?.email}
          phone={payout?.vendor_id?.phone_number}
        />
        <PersonCard
          title="Paid By"
          name={getPaidByName(payout)}
          email={payout?.paid_by?.email}
          phone={payout?.paid_by?.phone_number}
        />
      </div>

      {summary ? (
        <div className="space-y-3">
          <p className="text-sm font-semibold">Summary</p>
          <div className="grid gap-3 md:grid-cols-2">
            <InfoCard label="Total Orders" value={String(summary?.totalOrders || 0)} />
            <InfoCard
              label="Total Net Total"
              value={formatAmount(summary?.totalNetTotal)}
            />
            <InfoCard
              label="Total Item Price"
              value={formatAmount(summary?.totalItemPrice)}
            />
            <InfoCard
              label="Total Delivery Price"
              value={formatAmount(summary?.totalDeliveryPrice)}
            />
            <InfoCard label="Total Tips" value={formatAmount(summary?.totalTips)} />
            <InfoCard
              label="Total Payout Amount"
              value={formatAmount(summary?.totalPayoutAmount)}
            />
          </div>
        </div>
      ) : null}

      <div className="space-y-3">
        <p className="text-sm font-semibold">
          Orders ({Array.isArray(payout?.orders) ? payout.orders.length : 0})
        </p>
        <div className="space-y-3">
          {Array.isArray(payout?.orders) && payout.orders.length ? (
            payout.orders.map((order) => (
              <OrderCard key={order?._id || order?.order_id} order={order} />
            ))
          ) : (
            <div className="rounded-lg border border-dashed p-4 text-sm">
              No orders found.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const VendorPayoutsContent = ({ earningData }) => {
  const [payouts, setPayouts] = useState([]);
  const [selectedPayoutDetails, setSelectedPayoutDetails] = useState(null);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const vendorId = useMemo(() => getVendorId(earningData), [earningData]);

  useEffect(() => {
    let isMounted = true;

    const fetchVendorPayouts = async () => {
      setIsLoadingList(true);
      setSelectedPayoutDetails(null);

      try {
        const response = await earningApis.getAllVendorPayouts({
          page: 1,
          limit: 10,
          sortOrder: "desc",
        });

        if (isMounted) {
          setPayouts(extractListFromResponse(response?.data));
        }
      } catch (error) {
        if (isMounted) {
          setPayouts([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingList(false);
        }
      }
    };

    fetchVendorPayouts();

    return () => {
      isMounted = false;
    };
  }, []);

  const handlePayoutClick = async (payout) => {
    const payoutId = getPayoutId(payout);
    if (!payoutId) {
      return;
    }

    setIsLoadingDetails(true);

    try {
      const response = await earningApis.getVendorPayoutDetailsById(
        payoutId,
        vendorId ? { vendor_id: vendorId } : {}
      );
      setSelectedPayoutDetails(extractObjectFromResponse(response?.data));
    } catch (error) {
      setSelectedPayoutDetails(null);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  if (selectedPayoutDetails) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setSelectedPayoutDetails(null)}
          className="rounded-md border px-3 py-2 text-sm font-medium"
        >
          Back To Payouts
        </button>
        <VendorPayoutDetailsView details={selectedPayoutDetails} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {isLoadingList ? (
        <div className="rounded-lg border border-dashed p-4 text-sm">
          Loading payouts...
        </div>
      ) : payouts.length ? (
        <div className="space-y-3">
          {payouts.map((payout, index) => {
            const payoutId = getPayoutId(payout) || `payout-${index}`;
            const amount = getPayoutAmount(payout);
            const vendorName = getVendorName(payout);
            const vendorEmail = payout?.vendor_id?.email || "N/A";
            const vendorPhone = payout?.vendor_id?.phone_number || "N/A";
            const orderCount = Array.isArray(payout?.orders) ? payout.orders.length : 0;
            const paidByName = getPaidByName(payout);
            const transactionReference =
              payout?.transaction_reference || "N/A";
            const paymentMethod = payout?.payment_method || "N/A";

            return (
              <button
                key={payoutId}
                type="button"
                onClick={() => handlePayoutClick(payout)}
                disabled={isLoadingDetails}
                className="w-full rounded-xl border p-4 text-left transition hover:border-green600 disabled:cursor-not-allowed"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1">
                    <p className="text-xs uppercase tracking-wide text-gray500">
                      Payout Id
                    </p>
                    <p className="text-sm font-semibold break-all">{payoutId}</p>
                  </div>
                  <div className="space-y-1 text-right">
                    <p className="text-xs uppercase tracking-wide text-gray500">
                      Status
                    </p>
                    <p className="text-sm font-semibold">{getPayoutStatus(payout)}</p>
                  </div>
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <div className="rounded-lg bg-gray100 p-3">
                    <p className="text-xs uppercase tracking-wide text-gray500">
                      Vendor
                    </p>
                    <p className="mt-1 text-sm font-semibold">{vendorName}</p>
                    <p className="mt-1 text-xs break-all">{vendorEmail}</p>
                    <p className="mt-1 text-xs">{vendorPhone}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray500">
                      Payout Date
                    </p>
                    <p className="mt-1 text-sm">{formatDate(getPayoutDate(payout))}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray500">
                      Amount
                    </p>
                    <p className="mt-1 text-sm font-semibold">
                      {amount === null ? "N/A" : formattedAmount(amount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray500">
                      Payment Method
                    </p>
                    <p className="mt-1 text-sm">{paymentMethod}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray500">
                      Transaction Reference
                    </p>
                    <p className="mt-1 text-sm break-all">{transactionReference}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray500">
                      Paid By
                    </p>
                    <p className="mt-1 text-sm">{paidByName}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray500">
                      Orders
                    </p>
                    <p className="mt-1 text-sm">{orderCount}</p>
                  </div>
                </div>
                {orderCount ? (
                  <div className="mt-3">
                    <p className="text-xs uppercase tracking-wide text-gray500">
                      Order Ids
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {payout.orders.map((orderId) => (
                        <span
                          key={orderId}
                          className="rounded-full border px-2 py-1 text-xs"
                        >
                          {orderId}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
                <div className="mt-3 text-right text-xs font-semibold text-green600">
                  Click to view full payout details
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed p-4 text-sm">
          No payouts found.
        </div>
      )}

      {isLoadingDetails ? (
        <div className="rounded-lg border border-dashed p-4 text-sm">
          Loading payout details...
        </div>
      ) : null}
    </div>
  );
};

const EarningModal = ({ handleClose, isEarningDetail, earningData, modalType }) => {
  console.log("earningData", earningData);

  if (isEarningDetail) {
    return (
      <>
        <SidebarField
          footer={false}
          handleClose={() => handleClose()}
          title={modalType === "vendorPayouts" ? "Vendor Payouts" : "Order Details"}
        >
          <div className="px-2">
            {modalType === "vendorPayouts" ? (
              <VendorPayoutsContent earningData={earningData} />
            ) : (
              <EarningDetails
                deliveryDetails={earningData?.orderId?.delivery_address}
                vendorDetails={earningData?.orderDetails?.vendor}
                ProductDeatils={earningData?.orderDetails}
              />
            )}
          </div>
        </SidebarField>
      </>
    );
  }

  return null;
};

export default EarningModal;
